import React, { useState, useRef, useEffect } from 'react';
import {
  MapPin, Home, Building, Users, Globe, Sparkles,
  ChevronDown, ArrowRight, CheckCircle, Target, Eye,
  Instagram, Linkedin, Twitter, TrendingUp, MessageCircle, X, Mail as LucideMail 
} from 'lucide-react';
import { FaPhone } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import heroImg from '../../assets/Images/homedash.png';
import founderImg from '../../assets/Images/founder.png';
import howItWorksImg from '../../assets/Images/diagram.png';
import propertiesImg from '../../assets/Images/property.png';

const images = {
  hero: heroImg,
  founder: founderImg,
  howItWorks: howItWorksImg,
  properties: propertiesImg
};




function CurrencySelector({ userCurrency, setUserCurrency, fxRates }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(userCurrency);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelectedCurrency(userCurrency);
  }, [userCurrency]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (currency) => {
    setSelectedCurrency(currency);
    setUserCurrency(currency);
    localStorage.setItem('preferredCurrency', currency);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className="px-3 py-2 bg-[var(--bg-black)] text-[var(--text-black)] border border-white/30 rounded-full text-xs focus:outline-none focus:ring-1 focus:ring-white/50 shadow-md hover:shadow-lg transition-shadow duration-200 flex items-center justify-between min-w-[80px]"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedCurrency || 'Currency'}
        <svg 
          className={`ml-2 w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute right-0 mt-1 w-full bg-[var(--bg-black)] border border-white/30 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
          {Object.keys(fxRates).map((currency) => (
            <div
              key={currency}
              className={`px-3 py-2 text-xs cursor-pointer hover:bg-pink-500/30 transition-colors duration-150 ${
                selectedCurrency === currency ? 'bg-pink-500/50' : ''
              }`}
              onClick={() => handleSelect(currency)}
            >
              {currency}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LanguageSelector({ i18n }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState(i18n.language);
  const dropdownRef = useRef(null);
  const languages = [
    { code: "kn", name: "Kinyarwanda" },
    { code: "sw", name: "Swahili" },
    { code: "en", name: "English" },
    { code: "lg", name: "Luganda" },
    { code: "zu", name: "Zulu" },
    { code: "fr", name: "Français" },
    { code: "ar", name: "العربية" },
    { code: "hi", name: "हिन्दी" },
    { code: "zh-Hant", name: "中文 (Traditional)" },
    { code: "zh-Hans", name: "中文 (Simplified)" },
    { code: "ak", name: "Akan" },
    { code: "am", name: "Amharic" },
    { code: "ay", name: "Aymara" },
    { code: "bm", name: "Bambara" },
    { code: "be", name: "Bemba" },
    { code: "bn", name: "Bengali" },
    { code: "ber", name: "Berber" },
    { code: "nl", name: "Dutch" },
    { code: "de", name: "German" }, 
    { code: "gik", name: "Gikuyu" },
    { code: "gr", name: "Greek" }, 
    { code: "es", name: "Spanish" },
    { code: "fi", name: "Finnish" },
    { code: "ha", name: "Hausa" },
    { code: "ig", name: "Igbo" },
    { code: "id", name: "Indonesian" },
    { code: "it", name: "Italian" },
    { code: "ja", name: "Japanese" },
    { code: "km", name: "Khmer" },
    { code: "ki", name: "Kikuyu" },
    { code: "ko", name: "Korean" },
    { code: "ht", name: "Haitian Creole" },
    { code: "mg", name: "Malagasy" },
    { code: "ml", name: "Malayalam" },
    { code: "mr", name: "Marathi" },
    { code: "ne", name: "Nepali" },
    { code: "om", name: "Oromo" },
    { code: "ps", name: "Pashto" },
    { code: "pl", name: "Polish" },
    { code: "pt", name: "Portuguese" },
    { code: "pa", name: "Punjabi" },
    { code: "qu", name: "Quechua" },
    { code: "ru", name: "Russian" },
    { code: "so", name: "Somali" },
    { code: "sq", name: "Albanian" },
    { code: "ta", name: "Tamil" },
    { code: "te", name: "Telugu" },
    { code: "th", name: "Thai" },
    { code: "ti", name: "Tigrinya" },
    { code: "tr", name: "Turkish" },
    { code: "ur", name: "Urdu" },
    { code: "vi", name: "Vietnamese" },
    { code: "wo", name: "Wolof" },
    { code: "xh", name: "Xhosa" },
    { code: "yo", name: "Yoruba" }
  ];

  const selectedLanguage = languages.find(lang => lang.code === selectedLang);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (code) => {
    setSelectedLang(code);
    i18n.changeLanguage(code);
    localStorage.setItem('language', code);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className="px-3 py-2 bg-pink-500 text-white border border-white/30 rounded-full text-xs focus:outline-none focus:ring-1 focus:ring-white/50 shadow-md hover:shadow-lg transition-shadow duration-200 flex items-center justify-between min-w-[100px]"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedLanguage?.name || 'Language'}
        <svg 
          className={`ml-2 w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute right-0 mt-1 w-full bg-[var(--bg-black)] border border-white/30 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
          {languages.map((lang) => (
            <div
              key={lang.code}
              className={`px-3 py-2 text-xs cursor-pointer hover:bg-pink-500/30 transition-colors duration-150 ${
                selectedLang === lang.code ? 'bg-pink-500/50' : ''
              }`}
              onClick={() => handleSelect(lang.code)}
            >
              {lang.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FAQItem({ question, answer, isOpen, onClick }) {
  return (
    <div className="border border-white/20 rounded-lg overflow-hidden bg-[var(--bg-black)] backdrop-blur-sm">
      <button
        onClick={onClick}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/5 transition-colors border-none bg-transparent"
      >
        <h3 className="font-medium text-sm text-[var(--text-black)] pr-4">{question}</h3>
        <ChevronDown 
          className={`flex-shrink-0 text-pink-500 transition-transform duration-300 ${
            isOpen ? 'rotate-180' : ''
          }`}
          size={18}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <p className="px-4 pb-3 text-xs text-[var(--text-black)]/80 leading-relaxed">
          {answer}
        </p>
      </div>
    </div>
  );
}

export default function About() {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [userCurrency, setUserCurrency] = useState('RWF');
  const [fxRates, setFxRates] = useState({});
  const { t, i18n } = useTranslation('About');


  const [isContactOpen, setIsContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [recaptchaReady, setRecaptchaReady] = useState(false);

  const COMPANY_EMAIL = 'pongoincb@gmail.com';
  const COMPANY_PHONE = '+250789544277';

  useEffect(() => {
    const fetchFxRates = async () => {
      try {
        // ✅ Removed trailing spaces in URL
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
        const data = await response.json();
        setFxRates(data.rates);
      } catch (err) {
        console.error('Failed to fetch FX rates:', err);
      }
    };
    fetchFxRates();
  }, []);

  const [imagesLoaded, setImagesLoaded] = useState({
    hero: false,
    founder: false,
    howItWorks: false,
    properties: false
  });

  const handleImageLoad = (imageKey) => {
    setImagesLoaded(prev => ({ ...prev, [imageKey]: true }));
  };


    useEffect(() => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (!siteKey) {
      setRecaptchaReady(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => {
          setRecaptchaReady(true);
        });
      }
    };

    document.head.appendChild(script);
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      if (!recaptchaReady) {
        alert('Security verification not ready. Please refresh the page.');
        return;
      }

      const recaptchaToken = await window.grecaptcha.execute(
        process.env.REACT_APP_RECAPTCHA_SITE_KEY,
        { action: 'contact' }
      );

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/contact/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...contactForm,
            recaptcha_token: recaptchaToken,
          }),
        }
      );

      if (response.ok) {
        setSubmitStatus('success');
        setContactForm({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => {
          setIsContactOpen(false);
          setSubmitStatus(null);
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      console.error('Contact submission failed:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      let cleaned = value.replace(/[^\d+]/g, '');
      if (cleaned.startsWith('++')) {
        cleaned = '+' + cleaned.substring(2).replace(/\+/g, '');
      } else if (cleaned.includes('+')) {
        cleaned = '+' + cleaned.replace(/\+/g, '');
      }
      setContactForm((prev) => ({ ...prev, [name]: cleaned }));
    } else {
      setContactForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleEmailClick = () => {
    setIsContactOpen(true); 
  };

  const handlePhoneClick = () => {
    window.location.href = `tel:${COMPANY_PHONE.replace(/\s/g, '')}`;
  };

  const faqItems = t('about.faq.items', { returnObjects: true });
  const faqs = Array.isArray(faqItems) ? faqItems : [];
  const storyParagraphs = t('about.story.paragraphs', { returnObjects: true });
  const paragraphs = Array.isArray(storyParagraphs) ? storyParagraphs : [];
  const whatIsPongoFeatures = t('about.whatIsPongo.features', { returnObjects: true });
  const features = Array.isArray(whatIsPongoFeatures) ? whatIsPongoFeatures : [];
  const whoIsForList = t('about.whoIsFor.list', { returnObjects: true });
  const whoList = Array.isArray(whoIsForList) ? whoIsForList : [];
  const founderBio = t('about.founder.bio', { returnObjects: true });
  const bioList = Array.isArray(founderBio) ? founderBio : [];

  return (
    <div className="min-h-screen transition-colors duration-300 bg-[var(--bg-black)]">
      <header className="sticky top-0 z-30 bg-[var(--bg-black)] border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-2 py-2 min-h-[64px]">
            {/* Left Side - Logo & Title */}
            <div className="flex flex-wrap items-center gap-2 max-w-full">
              <Users className="text-pink-500 flex-shrink-0" size={24} />
              <div className="max-w-[140px] xs:max-w-[160px] sm:max-w-none overflow-x-auto whitespace-nowrap no-scrollbar">
                <h1 className="text-xs xs:text-sm text-[var(--text-black)] bg-white/10 rounded-full px-3 py-1 inline-block">
                  {t('about.header.title')}
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <CurrencySelector
                userCurrency={userCurrency}
                setUserCurrency={setUserCurrency}
                fxRates={fxRates}
              />
              <LanguageSelector
                i18n={i18n}
              />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Hero Section */}
        <section className="relative overflow-hidden py-8 sm:py-12 rounded-2xl bg-gradient-to-br from-pink-500/5 to-purple-500/5">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/30 rounded-full mb-4">
                <Sparkles className="text-pink-500" size={14} />
                <span className="text-xs font-medium text-pink-500">{t('about.header.subtitle')}</span>
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 text-[var(--text-black)] leading-tight">
                {t('about.header.subtitle')}
              </h1>
              <p className="text-sm sm:text-base text-[var(--text-black)]/80 mb-6 leading-relaxed">
                {t('about.hero.description')}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/explore">
                  <button className="px-6 py-2 bg-[var(--bg-black)] border border-white/60 text-[var(--text-black)] rounded-full text-sm font-medium 
                            transition-all duration-300 hover:shadow-[0_0_12px_4px_var(--image-nav-glow)] hover:border-white/80 hover:scale-[1.02]">
                    {t('about.hero.buttons.browse')}
                    <ArrowRight size={16} />
                  </button>
                </Link>
                <Link to="/">
                  <button className="px-6 py-2 bg-[var(--bg-black)] border border-white/60 text-[var(--text-black)] rounded-full text-sm font-medium 
                            transition-all duration-300 hover:shadow-[0_0_12px_4px_var(--image-nav-glow)] hover:border-white/80 hover:scale-[1.02]">
                    {t('about.hero.buttons.getStarted')}
                  </button>
                </Link>
              </div>
            </div>

            <div className="relative group">
              {!imagesLoaded.hero && (
                <div className="absolute inset-0 bg-[var(--bg-black)]/50 backdrop-blur-sm rounded-xl flex items-center justify-center animate-pulse">
                  <Sparkles className="text-pink-500" size={32} />
                </div>
              )}
              <img
                src={images.hero}
                alt={t('propertiesGallery.imageAlt')}
                className={`w-full rounded-xl shadow-2xl border border-white/10 transition-all duration-500 group-hover:shadow-pink-500/25 ${
                  imagesLoaded.hero ? 'opacity-100' : 'opacity-0'
                }`}
                loading="lazy"
                onLoad={() => handleImageLoad('hero')}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-pink-500/20 to-purple-500/20 flex items-center justify-center" style={{ display: 'none' }}>
                <Home className="text-[var(--text-black)]" size={48} />
              </div>
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section className="py-10 border-t border-white/10">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">{t('about.story.title')}</h2>
            <div className="space-y-4">
              {paragraphs.map((p, idx) => (
                <p key={idx} className="text-sm text-[var(--text-black)] leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </section>


        <section className="py-10 border-t border-white/10">
          <h2 className="text-xl sm:text-2xl font-bold mb-8 text-center">{t('about.whatIsPongo.title')}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="group relative bg-[var(--bg-black)] border border-white/20 rounded-lg p-4 overflow-hidden hover:border-pink-500/70 transition-all duration-500"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-500 rounded-lg flex items-center justify-center mb-3 shadow-md">
                  {idx === 0 ? <Home className="text-[var(--text-black)]" size={24} /> :
                  idx === 1 ? <TrendingUp className="text-[var(--text-black)]" size={24} /> :
                  idx === 2 ? <Building className="text-[var(--text-black)]" size={24} /> :
                  <MessageCircle className="text-[var(--text-black)]" size={24} />}
                </div>
                <h3 className="font-semibold text-sm mb-2 text-white">{feature.title}</h3>
                <p className="text-xs text-[var(--text-black)]/70 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </section>


        {/* How Pongo Works */}
         <section className="py-10 border-t border-white/10">
          <h2 className="text-xl sm:text-2xl font-bold mb-8 text-center">{t('about.howItWorks.title')}</h2>
          <div className="max-w-4xl mx-auto">
            <div className="relative mb-8 group">
              {!imagesLoaded.howItWorks && (
                <div className="absolute inset-0 bg-[var(--bg-black)]/50 backdrop-blur-sm rounded-xl flex items-center justify-center animate-pulse">
                  <TrendingUp className="text-pink-500" size={32} />
                </div>
              )}
              <img
                src={images.howItWorks}
                alt={t('howItWorks.imageAlt')}
                className={`w-full rounded-xl shadow-xl border border-white/10 mx-auto max-w-3xl transition-all duration-500 ${
                  imagesLoaded.howItWorks ? 'opacity-100' : 'opacity-0'
                }`}
                loading="lazy"
                onLoad={() => handleImageLoad('howItWorks')}
              />
            </div>
          </div>
        </section>

        {/* Real Properties Gallery */}
        <section className="py-10 border-t border-white/10">
          <h2 className="text-xl sm:text-2xl font-bold mb-8 text-center">{t('about.propertiesGallery.title')}</h2>
          <div className="relative group">
            {!imagesLoaded.properties && (
              <div className="absolute inset-0 bg-[var(--bg-black)]/50 backdrop-blur-sm rounded-xl flex items-center justify-center animate-pulse">
                <Building className="text-pink-500" size={32} />
              </div>
            )}
            <img
              src={images.properties}
              alt={t('propertiesGallery.imageAlt')}
              className={`w-full rounded-xl shadow-2xl border border-white/10 transition-all duration-500 ${
                imagesLoaded.properties ? 'opacity-100' : 'opacity-0'
              }`}
              loading="lazy"
              onLoad={() => handleImageLoad('properties')}
            />
          </div>
          <p className="text-center text-xs text-[var(--text-black)]/60 mt-4">
            {t('about.propertiesGallery.caption')}
          </p>
        </section>

        {/* Mission & Vision */}
        <section className="py-10 border-t border-white/10">
          <div className="grid lg:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="group bg-[var(--bg-black)] border border-white/20 rounded-lg p-6 backdrop-blur-sm hover:border-pink-500/50 transition-colors">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-500 rounded-lg flex items-center justify-center mb-4 shadow-md">
                <Target className="text-[var(--text-black)]" size={24} />
              </div>
              <h3 className="text-lg font-bold mb-3">{t('about.missionVision.mission.title')}</h3>
              <p className="text-xs sm:text-sm text-[var(--text-black)]/80 leading-relaxed">
                {t('about.missionVision.mission.description')}
              </p>
            </div>
            <div className="group bg-[var(--bg-black)] border border-white/20 rounded-lg p-6 backdrop-blur-sm hover:border-purple-500/50 transition-colors">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mb-4 shadow-md">
                <Eye className="text-[var(--text-black)]" size={24} />
              </div>
              <h3 className="text-lg font-bold mb-3">{t('about.missionVision.vision.title')}</h3>
              <p className="text-xs sm:text-sm text-[var(--text-black)]/80 leading-relaxed">
                {t('about.missionVision.vision.description')}
              </p>
            </div>
          </div>
        </section>

        {/* Who Pongo Is For */}
        <section className="py-10 border-t border-white/10">
          <h2 className="text-xl sm:text-2xl font-bold mb-8 text-center">{t('about.whoIsFor.title')}</h2>
          <div className="grid sm:grid-cols-2 gap-3 max-w-3xl mx-auto">
            {whoList.map((text, idx) => (
              <div
                key={idx}
                className="flex items-start gap-2 p-3 bg-[var(--bg-black)] border border-white/20 rounded-lg hover:border-pink-500/50 transition-colors group"
              >
                <CheckCircle className="text-pink-500 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform" size={18} />
                <p className="text-xs text-[var(--text-black)]/80">{text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* About the Founder */}
        <section className="py-10 border-t border-white/10">
          <h2 className="text-xl sm:text-2xl font-bold mb-8 text-center">{t('about.founder.title')}</h2>
          <div className="bg-[var(--bg-black)] border border-white/20 rounded-lg p-6 backdrop-blur-sm shadow-lg max-w-3xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center">
              <div className="w-full sm:w-48 flex-shrink-0 relative group">
                {!imagesLoaded.founder && (
                  <div className="absolute inset-0 bg-[var(--bg-black)]/50 backdrop-blur-sm rounded-lg flex items-center justify-center animate-pulse">
                    <Users className="text-pink-500" size={32} />
                  </div>
                )}
                <img
                  src={images.founder}
                  alt={t('about.founder.name')}
                  className={`w-48 h-48 rounded-lg object-cover shadow-lg border-4 border-white/20 transition-all duration-500 group-hover:shadow-pink-500/50 group-hover:scale-105 ${
                    imagesLoaded.founder ? 'opacity-100' : 'opacity-0'
                  }`}
                  loading="lazy"
                  onLoad={() => handleImageLoad('founder')}
                />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold mb-1">{t('about.founder.name')}</h3>
                <p className="text-pink-500 text-sm font-medium mb-3">{t('about.founder.role')}</p>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="text-pink-500" size={16} />
                  <span className="text-xs text-[var(--text-black)]/70">{t('about.founder.location')}</span>
                </div>
                {bioList.map((bio, idx) => (
                  <p key={idx} className="text-xs sm:text-sm text-[var(--text-black)]/80 leading-relaxed mb-4">
                    {bio}
                  </p>
                ))}
                <div className="flex gap-3">
                  <a href="https://www.linkedin.com/in/philippe-mugisha-934470272/" target="_blank" rel="noopener noreferrer" className="p-2 bg-white/5 border border-white/20 rounded-lg hover:border-pink-500/50 transition-colors">
                    <Linkedin className="text-pink-500" size={18} />
                  </a>
                  <a href="https://x.com/pongoinc" target="_blank" rel="noopener noreferrer" className="p-2 bg-white/5 border border-white/20 rounded-lg hover:border-pink-500/50 transition-colors">
                    <Twitter className="text-pink-500" size={18} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-10 border-t border-white/10">
          <h2 className="text-xl sm:text-2xl font-bold mb-8 text-center">{t('about.faq.title')}</h2>
          <div className="space-y-3 max-w-3xl mx-auto">
            {faqs.map((faq, idx) => (
              <FAQItem
                key={idx}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === idx}
                onClick={() => setOpenFAQ(openFAQ === idx ? null : idx)}
              />
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-10 border-t border-white/10">
          <div className="max-w-3xl mx-auto text-center">
            <Globe className="mx-auto mb-4 text-pink-500" size={36} />
            <h2 className="text-xl sm:text-2xl font-bold mb-4">{t('about.cta.title')}</h2>
            <p className="text-xs sm:text-sm text-[var(--text-black)]/80 mb-6 leading-relaxed">
              {t('about.cta.description')}
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <a href="https://chat.whatsapp.com/JG468c8TqSTHKOgMkwQl6g" target="_blank" rel="noopener noreferrer">
                <button className="px-6 py-2 bg-[var(--bg-black)] border border-white/60 text-[var(--text-black)] rounded-full text-sm font-medium transition-all duration-300 hover:shadow-[0_0_12px_4px_var(--image-nav-glow)] hover:border-white/80 hover:scale-[1.02]">
                  {t('about.cta.buttons.getStarted')}
                  <ArrowRight size={16} />
                </button>
              </a>
              <button
                onClick={() => setIsContactOpen(true)}
                className="px-6 py-2 bg-[var(--bg-black)] border border-white/60 text-[var(--text-black)] rounded-full text-sm font-medium transition-all duration-300 hover:shadow-[0_0_12px_4px_var(--image-nav-glow)] hover:border-white/80 hover:scale-[1.02]"
              >
                {t('about.cta.buttons.contactUs')}
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* CONTACT MODAL */}
      {isContactOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setIsContactOpen(false)}
        >
          <div
            className="bg-[var(--bg-black)] border border-[var(--border)] border-solid rounded-xl w-full max-w-md p-6 mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-[var(--text-black)]">{t('cta.buttons.contactUs')}</h2>
              <button
                onClick={() => {
                  setIsContactOpen(false);
                  setSubmitStatus(null);
                }}
                className="text-[var(--text-black)] hover:text-pink-500 bg-transparent font-bold border-none"
              >
                <X size={20} />
              </button>
            </div>
            {submitStatus === 'success' ? (
              <div className="text-green-500 text-center py-4">
                {t('contact.successMessage', 'Your message has been sent successfully!')}
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <input
                  type="text"
                  name="name"
                  placeholder={t('contact.form.name', 'Your Name')}
                  value={contactForm.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 bg-[var(--bg-black)] border border-[var(--border)] border-solid rounded-lg text-[var(--text-black)] focus:outline-none focus:ring-1 focus:ring-pink-500"
                />
                <input
                  type="email"
                  name="email"
                  placeholder={t('contact.form.email', 'Your Email')}
                  value={contactForm.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 bg-[var(--bg-black)] border border-[var(--border)] border-solid rounded-lg text-[var(--text-black)] focus:outline-none focus:ring-1 focus:ring-pink-500"
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder={t('contact.form.phone', 'Your Phone (Optional)')}
                  value={contactForm.phone}
                  onChange={handleInputChange}
                  inputMode="numeric"
                  pattern="[0-9+\s\-()]*"
                  className="w-full px-3 py-2 bg-[var(--bg-black)] border border-[var(--border)] border-solid rounded-lg text-[var(--text-black)] focus:outline-none focus:ring-1 focus:ring-pink-500"
                />
                <textarea
                  name="message"
                  placeholder={t('contact.form.message', 'Your Message')}
                  value={contactForm.message}
                  onChange={handleInputChange}
                  rows="4"
                  required
                  className="w-full px-3 py-2 bg-[var(--bg-black)] border border-[var(--border)] border-solid rounded-lg text-[var(--text-black)] focus:outline-none focus:ring-1 focus:ring-pink-500"
                />
                <button
                  type="submit"
                  disabled={isSubmitting || !recaptchaReady}
                  className="w-full py-2.5 bg-pink-500 border-none text-white rounded-lg font-medium hover:bg-pink-600 transition disabled:opacity-60"
                >
                  {!recaptchaReady
                    ? t('contact.securityChecking', 'Checking Security...')
                    : isSubmitting
                    ? t('contact.sending', 'Sending...')
                    : t('contact.sendMessage', 'Send Message')}
                </button>
              </form>
            )}
            {submitStatus === 'error' && (
              <div className="text-red-500 text-sm text-center mt-2">
                {t('contact.errorMessage', 'Failed to send message. Please try again.')}
              </div>
            )}
            <div className="text-center text-xs text-[var(--text-muted)] mt-4 pt-4 border-t border-[var(--border)]">
              <button
                onClick={handlePhoneClick}
                className="flex items-center justify-center gap-1 text-[var(--text-muted)] hover:text-pink-500 transition-colors bg-transparent border-none mx-auto mb-2"
              >
                <FaPhone size={10} />
                <span>{COMPANY_PHONE}</span>
              </button>
              <button
                onClick={handleEmailClick}
                className="flex items-center justify-center gap-1 text-[var(--text-muted)] hover:text-pink-500 transition-colors bg-transparent border-none mx-auto"
              >
                <LucideMail size={14} />
                <span>{COMPANY_EMAIL}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-[var(--bg-black)] border-t border-white/10 py-8 mt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="text-pink-500" size={20} />
                <span className="font-bold text-sm">{t('about.footer.company.name')}</span>
              </div>
              <p className="text-[var(--text-black)]/70 text-xs mb-3">
                {t('about.footer.company.description')}
              </p>
              <div className="flex gap-2">
                <a href="https://www.linkedin.com/company/pongoinc/" target="_blank" rel="noopener noreferrer" className="p-2 bg-white/5 border border-white/20 rounded-lg hover:border-pink-500/50 transition-colors">
                  <Linkedin className="text-pink-500" size={16} />
                </a>
                <a href="https://www.instagram.com/pongoinc/" target="_blank" rel="noopener noreferrer" className="p-2 bg-white/5 border border-white/20 rounded-lg hover:border-pink-500/50 transition-colors">
                  <Instagram className="text-pink-500" size={16} />
                </a>
                <a href="https://x.com/pongoinc" target="_blank" rel="noopener noreferrer" className="p-2 bg-white/5 border border-white/20 rounded-lg hover:border-pink-500/50 transition-colors">
                  <Twitter className="text-pink-500" size={16} />
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-sm mb-3 text-[var(--text-black)]">{t('about.footer.product.title')}</h4>
              <div className="space-y-1.5">
                <Link to="/" className="block text-[var(--text-black)] hover:text-pink-500 no-underline text-xs">{t('about.footer.product.links.home')}</Link>
                <Link to="/explore" className="block text-[var(--text-black)] hover:text-pink-500 no-underline text-xs">{t('about.footer.product.links.explore')}</Link>
                <Link to="/login" className="block text-[var(--text-black)] hover:text-pink-500 no-underline text-xs">{t('about.footer.product.links.listProperty')}</Link>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-sm mb-3 text-[var(--text-black)]">{t('about.footer.companyLinks.title')}</h4>
              <div className="space-y-1.5">
                <Link to="/about" className="block text-[var(--text-black)] hover:text-pink-500 no-underline text-xs">{t('about.footer.companyLinks.links.about')}</Link>
                <button onClick={() => setIsContactOpen(true)} className="block text-[var(--text-black)] hover:text-pink-500 text-left bg-transparent border-none cursor-pointer text-xs">
                  {t('about.footer.companyLinks.links.careers')}
                </button>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-sm mb-3 text-[var(--text-black)]">{t('about.footer.support.title')}</h4>
              <div className="space-y-1.5">
                <button onClick={() => setIsContactOpen(true)} className="block text-[var(--text-black)] hover:text-pink-500 text-left bg-transparent border-none cursor-pointer text-xs">
                  {t('about.footer.support.links.helpCenter')}
                </button>
                <button onClick={() => setIsContactOpen(true)} className="block text-[var(--text-black)] hover:text-pink-500 text-left bg-transparent border-none cursor-pointer text-xs">
                  {t('about.footer.support.links.contactUs')}
                </button>
                <Link to="/terms-and-privacy" className="block text-[var(--text-black)] hover:text-pink-500 no-underline text-xs">
                  {t('about.footer.support.links.privacyPolicy')}
                </Link>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 text-center text-xs text-[var(--text-black)]/60">
            {t('about.footer.company.copyright')}
          </div>
        </div>
      </footer>
    </div>
  );
}